/*
 * Copyright 2024 The Kokabo Authors. All rights reserved.
 * Use of this source code is governed by a BSD-style
 * license that can be found in the LICENSE file.
 */

import {Utils} from "../utils/Utils.js";
import {Log} from "../utils/Log.js";
import {SecurityPolicy, DefaultSecurityPolicy} from "./SecurityPolicy.js";


/**
 * E2EE facade used by hub and clients.
 *
 * The contract is intentionally explicit:
 * - E2EE is applied to payloads and selected headers when enabled by policy.
 * - A Diffie-Hellman exchange bootstraps the symmetric AES key when not already cached.
 * - Callers may opt out globally (policy) or via a sentinel key to retain compatibility with insecure deployments.
 * @constructor
 */
export class SecurityAgent {

    static NO_AES_KEY = "";

    static AES_KEY_SIZE = 256;


    /**
     * Policy must match the Go server side; divergence breaks interoperability.
     * @type {SecurityPolicy}
     */
    static policy = DefaultSecurityPolicy;

    static applyPolicy(policy) {
        SecurityAgent.policy = policy;
        if (policy.optOutE2EE) {
            SecurityAgent.KeyStorage.set("*", SecurityAgent.NO_AES_KEY);
        }
    }

    /**
     * @type {Map<string, CryptoKey|String>}
     */
    static KeyStorage = new Map();


    /***
     * Returns a symmetric key for (destination, clientUUID), reusing cached material when possible.
     * @param clientUUID {string}
     * @param keyExchangeEndpoint {string|URL}
     * @param destination {string}
     * @param code {string}
     * @return {Promise<{aesKey: CryptoKey|string, storageIndex: string}>}
     */
    static async getSymmetricKey(clientUUID, keyExchangeEndpoint, destination, code = "") {
        let urlString;
        if (typeof destination === "object") {
            urlString = destination.toString();
        } else {
            urlString = destination;
        }
        const search = `${urlString}+${clientUUID}`
        let aesKey;
        let storageIndex;
        let o = SecurityAgent.findSymKeyFor(search);
        if (o.found) {
            aesKey = o.key;
            storageIndex = o.idx;
        } else {
            const r = await SecurityAgent.registerSecureUUID(clientUUID, keyExchangeEndpoint, destination, code);
            aesKey = r.key;
            storageIndex = r.idx;
        }
        return Promise.resolve({aesKey: aesKey, storageIndex: storageIndex});
    }

    /**
     * Lookup strategy is prefix-based to allow destination normalization while still reusing keys.
     * @param search {string}
     * @return {{key: CryptoKey|string, idx: string, found: boolean}}
     * @constructor
     */
    static findSymKeyFor(search) {
        let symKey, storageIndex;
        let ok = SecurityAgent.KeyStorage.hasOwnProperty(search);
        if (!ok) {
            for (let k in SecurityAgent.KeyStorage) {
                if (k.startsWith(search)) {
                    symKey = SecurityAgent.KeyStorage[k];
                    storageIndex = k;
                    ok = true;
                    break;
                }
            }
            if (!ok) {
                // Wildcard key is used for full opt-out scenarios (policy-controlled).
                if (SecurityAgent.KeyStorage.has("*")) {
                    symKey = SecurityAgent.KeyStorage["*"];
                    storageIndex = "*";
                    ok = true;
                }
            }
        } else {
            symKey = SecurityAgent.KeyStorage[search];
            storageIndex = search;
        }
        return {key: symKey, idx: storageIndex, found: ok};
    }

    /**
     * Performs the two-step key exchange to bootstrap an AES-GCM key for the given UUID/destination.
     * @param clientUUID {string}
     * @param keyExchangeEndpoint {string|URL}
     * @param destination {string|URL}
     * @param code {string}
     * @returns {Promise<{key: CryptoKey|string, idx: string}>}
     * @throws {Error}
     */
    static async registerSecureUUID(clientUUID, keyExchangeEndpoint, destination = "", code = "") {

        if (!clientUUID || clientUUID === "") {
            throw new Error("SecurityAgent.RegisterSecureUUID: the clientUUID is not defined");
        }

        let hubName = "";
        if (destination !== "") {
            let url;
            if (typeof destination === "string") {
                url = new URL(destination);
            }
            if (url.protocol.startsWith("ws")) {
                hubName = url.pathname.replace("/api/v1/actors/connect/to/", "");
            } else if (url.protocol.startsWith("http")) {
                hubName = url.pathname.replace("/api/v1/actors/sse/subscribe/to/", "");
            }
        }

        //////////////////////////////////
        // Step 1
        // Diffie-Hellman key exchange
        //////////////////////////////////

        let algoECDHP256 = {name: "ECDH", namedCurve: "P-256"};

        let keyPair = await window.crypto.subtle.generateKey(algoECDHP256, true, ["deriveKey"]);

        let exportedKey = await window.crypto.subtle.exportKey("spki", keyPair.publicKey);
        let base64Key = arrayBufferToBase64(exportedKey);

        const si = `${destination}+${clientUUID}`;
        const sha256CheckSum = await Utils.sha256(si);

        /**
         * Step1 binds the exchange to (destination, clientUUID) via checksum to prevent replay/misassociation.
         * @type {{clientPublicKey: (string),checksum: string}}
         */
        let step1 = {
            clientPublicKey: base64Key,
            checksum: sha256CheckSum
        }

        const step1Url = `${keyExchangeEndpoint}/1/${hubName}`;
        if (Log.supports(Log.Core)) {
            Log.core("SecurityAgent.RegisterSecureUUID step1", step1Url, JSON.stringify(step1));
        }
        const response = await fetch(step1Url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'omit', // Don't leak cookies before a secure channel exists.
            body: JSON.stringify(step1)
        });

        if (response.status !== 200) {
            const responseText = await response.text();
            const message = `SecurityAgent.RegisterSecureUUID step1 failed ${step1Url} StatusCode: ${response.status} Message: ${responseText}`;
            return Promise.reject(new Error(message));
        }


        /**
         * @type { {serverPublicKey: string, storageIndex:string} }
         */
        let step1Response = await response.json();
        if (Log.supports(Log.Core)) {
            Log.core("SecurityAgent.RegisterSecureUUID step1Response", JSON.stringify(step1Response));
        }
        /**
         * @type {CryptoKey}
         */
        let importedKey;
        try {
            const serverPublicKeyBase64 = step1Response.serverPublicKey;
            const serverPublicKey = base64ToArrayBuffer(serverPublicKeyBase64);
            importedKey = await window.crypto.subtle.importKey("spki", serverPublicKey, algoECDHP256, false, []);
        } catch (e) {
            Log.error("SecurityAgent.RegisterSecureUUID window.crypto.subtle.importKey", e);
        }

        /**
         *  @type {CryptoKey}
         */
        let tmpDerivedKey;
        try {
            tmpDerivedKey = await window.crypto.subtle.deriveKey(
                {name: "ECDH", public: importedKey}, keyPair.privateKey,
                {name: "AES-GCM", length: SecurityAgent.AES_KEY_SIZE},
                true,
                ["encrypt", "decrypt"]
            );
        } catch (e) {
            Log.error("SecurityAgent.RegisterSecureUUID window.crypto.subtle.deriveKey", e);
        }

        // Optional verification defends against key exchange confusion/misrouting when enforced by policy.
        if (SecurityAgent.policy.enforcedE2EEVerification) {
            try {
                let decryptedMessage = await decrypt(tmpDerivedKey, step1Response.storageIndex);
                if (decryptedMessage !== step1.checksum) {
                    Log.error("SecurityAgent.RegisterSecureUUID EnforcedVerifications optional deciphering verification of the checksum miss match:", step1Response.storageIndex, step1.checksum)
                } else {
                    Log.core("SecurityAgent.RegisterSecureUUID EnforcedVerifications optional deciphering verification of the checksum did succeed")
                }
            } catch (e) {
                Log.error("SecurityAgent.RegisterSecureUUID EnforcedVerifications optional deciphering verification of the checksum", e);
            }
        }


        ////////////////////////////
        // Step 2
        // Ask for the SymKey
        ///////////////////////////


        const payload = {
            clientUUID: clientUUID,
            destination: destination,
            code: code,
        }

        const jsonPayload = JSON.stringify(payload);
        const cipheredPayload = await encrypt(tmpDerivedKey, jsonPayload);

        const symKeyRequest = {
            storageIndex: step1Response.storageIndex,
            payload: cipheredPayload,
        }

        const step2Url = `${keyExchangeEndpoint}/2/${hubName}`;
        const response2 = await fetch(step2Url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'omit', // Don't leak cookies before a secure channel exists.
            body: JSON.stringify(symKeyRequest)
        });
        if (response2.status !== 200) {
            const responseText = await response2.text();
            const message = `SecurityAgent.RegisterSecureUUID step2 failed ${step2Url}StatusCode: ${response2.status} Message: ${responseText}`;
            return Promise.reject(new Error(message));
        }

        /**
         * @type { {symKey: string, storageIndex: string} }
         */
        const symKeyResponse = await response2.json();

        /**
         * @type {ArrayBuffer}
         */
        let decipheredSymKey;
        try {
            decipheredSymKey = await bufferDecrypt(tmpDerivedKey, symKeyResponse.symKey);
        } catch (e) {
            const message = `SecurityAgent.RegisterSecureUUID decrypt symKeyResponse.symKey with the sharedKey failed Message: ${e.message}`;
            return Promise.reject(new Error(message));
        }

        let symKey;
        try {
            symKey = await window.crypto.subtle.importKey("raw", decipheredSymKey, "AES-GCM", true, ["encrypt", "decrypt"]);
        } catch (e) {
            const message = `SecurityAgent.RegisterSecureUUID crypto importKey failed Message: ${e.message}`;
            return Promise.reject(new Error(message));
        }

        // Persist the negotiated key so subsequent calls skip the exchange (reduces latency and hub load).
        SecurityAgent.KeyStorage.set(symKeyResponse.storageIndex, symKey);
        Log.info("SecurityAgent.RegisterSecureUUID storing the key for: ", symKeyResponse.storageIndex);
        return Promise.resolve({idx: symKeyResponse.storageIndex, key: symKey});
    }


    /**
     * Secure fetch wrapper.
     *
     * Failure-mode note: the server sets "Not-Ciphered: true" on ciphering errors so callers can still decode structured JSON errors.
     *
     * @param clientUUID {string}
     * @param aesKey {CryptoKey|string}
     * @param input {RequestInfo|URL}
     * @param init {RequestInit|{headers: Headers}}
     * @return {Promise<Response>}
     * @throws {Error}
     */
    static async fetch(clientUUID, aesKey, input, init = null) {
        if (!clientUUID || clientUUID === "") {
            throw new Error("The clientUUID is not defined");
        }
        if (init === null) {
            init = {headers: new Headers()}
        }
        init.headers.set('kokabo-uuid', clientUUID);
        if (aesKey === SecurityAgent.NO_AES_KEY) {
            return fetch(input, init)
        } else {
            try {
                if (init.body) {
                    const body = init.body;
                    init.body = await SecurityAgent.encrypt(aesKey, body);
                }
                for (let [key, value] of init.headers.entries()) {
                    if (SecurityAgent.policy.headersToCipher.has(key)) {
                        const v = await SecurityAgent.encrypt(aesKey, value);
                        init.headers.set(key, v);
                    }
                }
            } catch (e) {
                Log.error("SecurityAgent.Fetch: Ciphering the request failed", e);
                return Promise.reject(e);
            }

            /*@type {Response} */
            let rawResponse;
            try {
                rawResponse = await fetch(input, init);
            } catch (e) {
                Log.error("SecurityAgent.Fetch: Fetch failed", e);
                return Promise.reject(e);
            }

            const notCiphered = rawResponse.headers.get("Not-Ciphered");
            if (notCiphered && notCiphered === "true") {
                if (rawResponse.ok) {
                    return Promise.resolve(rawResponse);
                } else {
                    try {
                        const jsonErr = await rawResponse.json();
                        return Promise.reject(new Error(jsonErr.message));
                    } catch (e) {
                        Log.error("SecurityAgent.Fetch: Not-Ciphered json decoding did fail", e);
                        return Promise.reject(e);
                    }
                }
            } else {
                let decipheredBody;
                try {
                    const body = await rawResponse.text();
                    decipheredBody = await SecurityAgent.decrypt(aesKey, body);
                    let response = new Response(decipheredBody, rawResponse);
                    try {
                        for (let [key, value] of response.headers.entries()) {
                            if (SecurityAgent.policy.headersToCipher.has(key)) {
                                const v = await SecurityAgent.decrypt(aesKey, value);
                                response.headers.set(key, v);
                            }
                        }
                        return Promise.resolve(response);
                    } catch (e) {
                        Log.error("SecurityAgent.Fetch: Deciphering the response headers failed", e);
                        return Promise.reject(e);
                    }
                } catch (e) {
                    Log.error("SecurityAgent.Fetch: Deciphering the response body failed exception:", e, rawResponse.status);
                    return Promise.reject(e);
                }
            }
        }
    }

    /**
     * @param aesKey{CryptoKey|string}
     * @param {*} payload
     * @return {Promise<string>}
     */
    static async encrypt(aesKey, payload) {
        return SecurityAgent._cipherOrDecipher(aesKey, payload, true);
    }


    /**
     * @param aesKey{CryptoKey|string}
     * @param {*} payload
     * @return {Promise<string>}
     */
    static async decrypt(aesKey, payload) {
        return SecurityAgent._cipherOrDecipher(aesKey, payload, false);
    }

    /**
     * Policy gate for ciphering/deciphering so callers don't need to special-case insecure deployments.
     *
     * @param {CryptoKey|string} aesKey
     * @param {*} payload
     * @param {boolean} cipher
     * @return {Promise<string>}
     * @private
     */
    static async _cipherOrDecipher(aesKey, payload, cipher) {
        if (SecurityAgent.policy.forceE2EEOverTLS || aesKey !== undefined) {
            if (aesKey === undefined) {
                throw new Error("The key is not defined");
            } else {
                if (aesKey === SecurityAgent.NO_AES_KEY) {
                    return payload;
                } else {
                    if (cipher) {
                        return await encrypt(aesKey, payload);
                    } else {
                        return await decrypt(aesKey, payload);
                    }
                }
            }
        } else {
            return Promise.resolve(payload);
        }
    }

    /***
     * @return {Promise<CryptoKey>}
     ***/
    static async newAes256Key() {
        return await crypto.subtle.generateKey(
            {
                name: "AES-GCM",
                length: 256,
            },
            true,
            ["encrypt", "decrypt"]
        );
    }

    /***
     * @param {CryptoKey|string} key
     **/
    static async convertCryptoKeyToBase64(key) {
        const exportedKey = await window.crypto.subtle.exportKey("raw", key);
        return arrayBufferToBase64(exportedKey);
    }

    /**
     * @param {string} base64Key
     * @return {Promise<CryptoKey>}
     */
    static async convertBase64ToCryptoKey(base64Key) {
        const rawKey = base64ToArrayBuffer(base64Key);
        return await window.crypto.subtle.importKey(
            "raw",
            rawKey,
            { name: "AES-GCM" },
            true,
            ["encrypt", "decrypt"]
        );
    }


    /**
     * Host parsing is strict to avoid silently accepting URLs with paths/search that would be security-sensitive for origin derivation.
     * @param fullHost {string|undefined}
     * @return {string}
     * @throws {Error}
     */
    static apiHostFrom(fullHost = undefined) {
        if (fullHost === undefined) {
            return window.location.origin;
        } else {
            fullHost = fullHost.replace("ws://", "http://").replace("wss://", "https://");
            const url = new URL(fullHost);
            if (url.protocol !== 'http:' && url.protocol !== 'https:') {
                throw new Error("SecurityAgent.apiHostFrom: The URL must use the HTTP or HTTPS protocol");
            }
            if (url.pathname !== '/' || url.search !== '' || url.hash !== '') {
                throw new Error("SecurityAgent.apiHostFrom: The URL must not have a path, search, or hash part");
            }
            return url.origin;
        }
    }

    /**
     * Host parsing is strict to avoid silently accepting URLs with paths/search that would be security-sensitive for origin derivation.
     * @param fullHost {string|undefined}
     * @return {string}
     * @throws {Error}
     */
    static wsHostFrom(fullHost = undefined) {
        if (fullHost === undefined) {
            return window.location.protocol.replace('http', 'ws') + '//' + window.location.host;
        } else {
            fullHost = fullHost.replace("http://", "ws://").replace("https://", "wss://");
            const url = new URL(fullHost);
            if (url.protocol !== 'ws:' && url.protocol !== 'wss:') {
                throw new Error("SecurityAgent.wsHostFrom: The URL must use the WS or WSS protocol as the basis for WebSocket connection");
            }
            if (url.pathname !== '/' || url.search !== '' || url.hash !== '') {
                throw new Error("SecurityAgent.wsHostFrom: The URL must not have a path, search, or hash part");
            }
            return url.origin;
        }
    }


    // Static queue to manage fetch requests
    static _fetchQueue = [];

    // Flag to indicate if a fetch is in progress
    static isFetching = false;

    /**
     * Serialized fetch queue prevents concurrent key-bound operations from interleaving in environments where ordering matters.
     * @param clientUUID {string}
     * @param aesKey {CryptoKey|string}
     * @param input {RequestInfo|URL}
     * @param init {RequestInit|{headers: Headers}}
     * @return {Promise<Response>}
     * @throws {Error}
     */
    static async fetchQueue(clientUUID, aesKey, input, init = null) {
        return new Promise((resolve, reject) => {
            SecurityAgent._fetchQueue.push({clientUUID, aesKey, input, init, resolve, reject});
            SecurityAgent.processQueue();
        });
    }

    static async processQueue() {
        if (SecurityAgent.isFetching || SecurityAgent._fetchQueue.length === 0) {
            return;
        }

        SecurityAgent.isFetching = true;

        const {clientUUID, aesKey, input, init, resolve, reject} = SecurityAgent._fetchQueue.shift();

        try {
            const response = await SecurityAgent.fetch(clientUUID, aesKey, input, init);
            resolve(response);
        } catch (error) {
            reject(error);
        } finally {
            SecurityAgent.isFetching = false;
            await SecurityAgent.processQueue();
        }
    }


}

//////////////////////
// Crypto utilities
//////////////////////

/**
 * @param {CryptoKey} sharedKey
 * @param {string} cipheredBase64String
 * @return {Promise<String>}
 */
async function decrypt(sharedKey, cipheredBase64String) {
    const decryptedContent = await bufferDecrypt(sharedKey, cipheredBase64String);
    return new TextDecoder().decode(decryptedContent);
}

/**
 * @param sharedKey
 * @param cipheredBase64String
 * @return {Promise<ArrayBuffer>}
 */
async function bufferDecrypt(sharedKey, cipheredBase64String) {
    const cipheredToken = base64ToArrayBuffer(cipheredBase64String);
    const nonce = cipheredToken.slice(0, 12);
    const cipheredString = cipheredToken.slice(12);
    return await window.crypto.subtle.decrypt(
        {name: "AES-GCM", iv: nonce, tagLength: 128},
        sharedKey,
        cipheredString
    );
}


/**
 * @param {CryptoKey} sharedKey
 * @param {string} plaintext
 * @return {Promise<String>}
 */
async function encrypt(sharedKey, plaintext) {
    const nonce = window.crypto.getRandomValues(new Uint8Array(12));
    const encoder = new TextEncoder();
    const plaintextBuffer = encoder.encode(plaintext);
    const ciphertext = await window.crypto.subtle.encrypt(
        {name: "AES-GCM", iv: nonce, tagLength: 128},
        sharedKey,
        plaintextBuffer
    );
    let result = new Uint8Array(nonce.length + ciphertext.byteLength);
    result.set(new Uint8Array(nonce), 0);
    result.set(new Uint8Array(ciphertext), nonce.length);
    return arrayBufferToBase64(result.buffer);
}


/**
 * @param buffer
 * @return {string}
 */
function arrayBufferToBase64(buffer) {
    try {
        let binary = '';
        let bytes = new Uint8Array(buffer);
        let len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    } catch (e) {
        Log.error("arrayBufferToBase64", e);
        return ""
    }
}

/**
 * @param base64
 * @return {ArrayBuffer}
 */
function base64ToArrayBuffer(base64) {
    try {
        const binaryString = atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    } catch (e) {
        Log.error("base64ToArrayBuffer", e);
        return new ArrayBuffer(0);
    }
}

function btoa(s) {
    return window.btoa(s);
}

function atob(s) {
    return window.atob(s);
}